import React from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Switch,
  Chip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsIcon from '@mui/icons-material/Payments';
import LanIcon from '@mui/icons-material/Lan';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

type CashDrawerPin = 'auto' | 'pin2' | 'pin5';

interface CashDrawerPrinterInfo extends DefinedPrinter {
  pin?: CashDrawerPin;
}

// ─── Ağ Yazıcısı (ESC/POS TCP 9100) Yönetimi ─────────────────────────
// Driver kurulumu gerektirmeyen ağ yazıcıları local-service'te tanımlanır;
// tanımlanan yazıcı "Etiket [tcp://ip:port]" adıyla yazıcı listesine düşer ve
// tüm seçim ekranlarında normal yazıcı gibi kullanılır. Yazdırma anında
// local-service tcp:// adını görüp spooler yerine doğrudan sokete gönderir.

interface NetworkPrinterEntry {
  name: string;
  host: string;
  port: number;
  label: string;
}

interface ScannedHost {
  host: string;
  port: number;
  alreadyRegistered: boolean;
}

const LOCAL_SERVICE_URL = 'http://localhost:3003';

const NetworkPrinterSection: React.FC<{ onPrintersChanged: () => void }> = ({ onPrintersChanged }) => {
  const { t } = useTranslation('salesSystem');
  const [expanded, setExpanded] = React.useState(false);
  const [entries, setEntries] = React.useState<NetworkPrinterEntry[]>([]);
  const [label, setLabel] = React.useState('');
  const [host, setHost] = React.useState('');
  const [port, setPort] = React.useState('9100');
  const [busy, setBusy] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [scanning, setScanning] = React.useState(false);
  const [scanResults, setScanResults] = React.useState<ScannedHost[] | null>(null);

  const loadEntries = React.useCallback(async () => {
    try {
      const res = await fetch(`${LOCAL_SERVICE_URL}/api/network-printers`);
      if (res.ok) setEntries(await res.json());
    } catch (_) {
      // Local-service kapalı olabilir; bölüm açılınca hata add/probe'da görünür
    }
  }, []);

  React.useEffect(() => {
    if (expanded) loadEntries();
  }, [expanded, loadEntries]);

  const handleScan = async () => {
    setScanning(true);
    setFeedback(null);
    setScanResults(null);
    try {
      const res = await fetch(`${LOCAL_SERVICE_URL}/api/network-printers/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setFeedback({ type: 'error', msg: data.message || t('printingSettingsTab.networkPrinters.scanFail') });
      } else {
        setScanResults(data.hosts || []);
        if ((data.hosts || []).length === 0) {
          setFeedback({ type: 'error', msg: t('printingSettingsTab.networkPrinters.scanEmpty') });
        }
      }
    } catch (_) {
      setFeedback({ type: 'error', msg: t('printingSettingsTab.networkPrinters.serviceUnreachable') });
    } finally {
      setScanning(false);
    }
  };

  const handlePickScanned = (item: ScannedHost) => {
    setHost(item.host);
    setPort(String(item.port));
    setFeedback(null);
  };

  const handleProbe = async () => {
    if (!host.trim()) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await fetch(`${LOCAL_SERVICE_URL}/api/network-printers/probe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host: host.trim(), port: parseInt(port, 10) || 9100 }),
      });
      const data = await res.json();
      setFeedback(data.success
        ? { type: 'success', msg: t('printingSettingsTab.networkPrinters.probeOk') }
        : { type: 'error', msg: t('printingSettingsTab.networkPrinters.probeFail', { detail: data.message || '' }) });
    } catch (e: any) {
      setFeedback({ type: 'error', msg: t('printingSettingsTab.networkPrinters.serviceUnreachable') });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!host.trim()) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await fetch(`${LOCAL_SERVICE_URL}/api/network-printers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim(), host: host.trim(), port: parseInt(port, 10) || 9100 }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setFeedback({ type: 'error', msg: data.message || t('printingSettingsTab.networkPrinters.addFail') });
      } else {
        setFeedback({ type: 'success', msg: t('printingSettingsTab.networkPrinters.addOk', { name: data.printer?.name || '' }) });
        const addedHost = host.trim();
        setScanResults((prev) => prev ? prev.map((h) => (h.host === addedHost ? { ...h, alreadyRegistered: true } : h)) : prev);
        setLabel('');
        setHost('');
        setPort('9100');
        await loadEntries();
        onPrintersChanged();
      }
    } catch (_) {
      setFeedback({ type: 'error', msg: t('printingSettingsTab.networkPrinters.serviceUnreachable') });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (entry: NetworkPrinterEntry) => {
    setBusy(true);
    try {
      await fetch(`${LOCAL_SERVICE_URL}/api/network-printers`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host: entry.host, port: entry.port }),
      });
      await loadEntries();
      onPrintersChanged();
    } catch (_) {
      // sessiz: liste yenilenemediyse mevcut kalır
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LanIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.1rem' }} />
          <Typography variant="subtitle2">{t('printingSettingsTab.networkPrinters.title')}</Typography>
        </Box>
        <Button size="small" variant="text" onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('printingSettingsTab.networkPrinters.hide') : t('printingSettingsTab.networkPrinters.manage')}
        </Button>
      </Box>
      {expanded && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('printingSettingsTab.networkPrinters.description')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={scanning ? <CircularProgress size={14} /> : <LanIcon sx={{ fontSize: '1rem' }} />}
              onClick={handleScan}
              disabled={scanning || busy}
            >
              {scanning ? t('printingSettingsTab.networkPrinters.scanning') : t('printingSettingsTab.networkPrinters.scan')}
            </Button>
            {scanning && (
              <Typography variant="caption" color="text.secondary">
                {t('printingSettingsTab.networkPrinters.scanHint')}
              </Typography>
            )}
          </Box>
          {scanResults && scanResults.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('printingSettingsTab.networkPrinters.scanResults', { count: scanResults.length })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {scanResults.map((item) => (
                  <Chip
                    key={`${item.host}:${item.port}`}
                    size="small"
                    color={item.alreadyRegistered ? 'default' : 'primary'}
                    variant={item.alreadyRegistered ? 'filled' : 'outlined'}
                    label={item.alreadyRegistered
                      ? t('printingSettingsTab.networkPrinters.scannedRegistered', { host: item.host })
                      : item.host}
                    onClick={item.alreadyRegistered ? undefined : () => handlePickScanned(item)}
                    disabled={item.alreadyRegistered}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <TextField
              size="small"
              label={t('printingSettingsTab.networkPrinters.labelField')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ width: 170 }}
            />
            <TextField
              size="small"
              label={t('printingSettingsTab.networkPrinters.ipField')}
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="192.168.1.50"
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              label={t('printingSettingsTab.networkPrinters.portField')}
              value={port}
              onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))}
              sx={{ width: 90 }}
            />
            <Button size="small" variant="outlined" onClick={handleProbe} disabled={busy || !host.trim()}>
              {t('printingSettingsTab.networkPrinters.probe')}
            </Button>
            <Button size="small" variant="contained" onClick={handleAdd} disabled={busy || !host.trim()}>
              {t('printingSettingsTab.networkPrinters.add')}
            </Button>
            {busy && <CircularProgress size={18} />}
          </Box>
          {feedback && <Alert severity={feedback.type} sx={{ mb: 1, py: 0 }}>{feedback.msg}</Alert>}
          {entries.length > 0 && (
            <List dense disablePadding component={Paper} variant="outlined" sx={{ maxHeight: 160, overflow: 'auto' }}>
              {entries.map((entry) => (
                <ListItem
                  key={`${entry.host}:${entry.port}`}
                  divider
                  secondaryAction={
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: '1rem' }} />}
                      onClick={() => handleRemove(entry)}
                      disabled={busy}
                      sx={{ fontSize: '0.7rem', p: '2px 4px' }}
                    >
                      {t('printingSettingsTab.common.remove')}
                    </Button>
                  }
                  sx={{ pl: 1.5, pr: 1, py: 0.5 }}
                >
                  <ListItemText primary={entry.name} sx={{ m: 0 }} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Paper>
  );
};

// Kategori tipi
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface PrintingSettingsTabProps {
  // Yazdırma ayarları
  printingMode: 'single' | 'category';
  defaultReceiptPrinter: DefinedPrinter | null;
  definedPrinters: DefinedPrinter[];
  categoryPrinterAssignments: { [categoryId: string]: string | null };
  // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
  categoryPrinterAssignments2?: { [categoryId: string]: string | null };
  
  // ✅ YENİ: Adisyon yazıcısı (kategori bazlı modda restoran için)
  adisyonPrinter?: DefinedPrinter | null;
  onAdisyonPrinterChange?: (printer: DefinedPrinter | null) => void;
  
  // ✅ YENİ: Kategorisiz ürün yazıcısı (kategori bazlı modda — kategorisi olmayan ürünler için)
  uncategorizedPrinter?: DefinedPrinter | null;
  onUncategorizedPrinterChange?: (printer: DefinedPrinter | null) => void;
  
  // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
  useBothFiscalAndReceiptPrinter?: boolean;
  onUseBothFiscalAndReceiptPrinterChange?: (enabled: boolean) => void;
  
  // ✅ YENİ: Restoran modu kontrolü (adisyon yazıcısını göstermek için)
  isRestaurantMode?: boolean;
  
  // Seçilmiş kategorilerin listesi ve bu kategorilere ait veriler
  uniqueCategoryIds: string[];
  categoriesToListForPrinting: InventoryCategory[];
  
  // Yerel yazıcı işlemleri
  localPrinters: DefinedPrinter[];
  loadingLocalPrinters: boolean;
  localPrintersError: string | null;
  
  // Event handler'lar
  onPrintingModeChange: (event: React.ChangeEvent<HTMLInputElement>, newMode: string) => void;
  onSetDefaultPrinter: (printer: DefinedPrinter) => void;
  onRemoveDefaultPrinter: () => void;
  onCategoryPrinterChange: (categoryId: string, printerId: string | null) => void;
  // ✅ YENİ: 2. yazıcı seçimi değiştiğinde (verilirse 2. yazıcı sütunu görünür)
  onCategoryPrinterChange2?: (categoryId: string, printerId: string | null) => void;
  onRefreshLocalPrinters: () => void;
  onPrintTestPage: (printerId: string, printerName: string) => void;
  
  // Test sayfası yazdırma durumu
  printTestLoading: string | null;

  // Fiş ayarları modalı için event handler
  onOpenReceiptSettingsModal: () => void;
  
  // Fiş numarası sıfırlama fonksiyonu
  onResetReceiptNumber?: () => void;
  
  // Fiş numarası ayarlarını gösterme durumu
  receiptNumberEnabled?: boolean;

  // ✅ YENİ: Para çekmecesi tetik yazıcısı (opsiyonel, callback verilirse UI görünür)
  cashDrawerPrinter?: CashDrawerPrinterInfo | null;
  onCashDrawerPrinterChange?: (printer: CashDrawerPrinterInfo | null) => void;
  /** Pin (auto/pin2/pin5) ile birlikte test eder. */
  onTestCashDrawer?: (printerName: string, pin: CashDrawerPin) => void;
  cashDrawerTestLoading?: boolean;
}

const PrintingSettingsTab: React.FC<PrintingSettingsTabProps> = ({
  printingMode,
  defaultReceiptPrinter,
  definedPrinters,
  categoryPrinterAssignments,
  categoryPrinterAssignments2,
  adisyonPrinter,
  onAdisyonPrinterChange,
  uncategorizedPrinter,
  onUncategorizedPrinterChange,
  useBothFiscalAndReceiptPrinter = false,
  onUseBothFiscalAndReceiptPrinterChange,
  isRestaurantMode = false,
  uniqueCategoryIds,
  categoriesToListForPrinting,
  localPrinters,
  loadingLocalPrinters,
  localPrintersError,
  onPrintingModeChange,
  onSetDefaultPrinter,
  onRemoveDefaultPrinter,
  onCategoryPrinterChange,
  onCategoryPrinterChange2,
  onRefreshLocalPrinters,
  onPrintTestPage,
  printTestLoading,
  onOpenReceiptSettingsModal,
  onResetReceiptNumber,
  receiptNumberEnabled = false,
  cashDrawerPrinter = null,
  onCashDrawerPrinterChange,
  onTestCashDrawer,
  cashDrawerTestLoading = false,
}) => {
  const { t } = useTranslation('salesSystem');
  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">{t('printingSettingsTab.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {receiptNumberEnabled && onResetReceiptNumber && (
            <Button 
              variant="outlined" 
              size="small"
              color="warning"
              onClick={onResetReceiptNumber}
            >
              {t('printingSettingsTab.receiptNumberResetButton')}
            </Button>
          )}
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<SettingsIcon />}
            onClick={onOpenReceiptSettingsModal}
          >
            {t('printingSettingsTab.receiptSettingsButton')}
          </Button>
        </Box>
      </Box>
      
      {/* Ağ yazıcısı (driver'sız ESC/POS TCP 9100) yönetimi */}
      <NetworkPrinterSection onPrintersChanged={onRefreshLocalPrinters} />

      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          row
          aria-label={t('printingSettingsTab.mode.ariaLabel')}
          name="printing-mode-radio-buttons-group"
          value={printingMode}
          onChange={onPrintingModeChange}
        >
          <FormControlLabel 
            value="single" 
            control={<Radio size="small" />} 
            label={<Box sx={{display: 'flex', alignItems: 'center'}}><PrintIcon sx={{mr: 0.5, fontSize: '1rem'}}/> {t('printingSettingsTab.mode.single')}</Box>} 
          />
          <FormControlLabel 
            value="category" 
            control={<Radio size="small" />} 
            label={<Box sx={{display: 'flex', alignItems: 'center'}}><CategoryIcon sx={{mr: 0.5, fontSize: '1rem'}}/> {t('printingSettingsTab.mode.category')}</Box>} 
            sx={{ml:2}}
          />
        </RadioGroup>
      </FormControl>
      <Divider sx={{ mb: 2 }}/>

      {/* Tek Yazıcı Modu İçeriği */}
      <Box sx={{ opacity: printingMode === 'single' ? 1 : 0.5, pointerEvents: printingMode === 'single' ? 'auto' : 'none' }}>
        {printingMode === 'single' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              {defaultReceiptPrinter ? (
                <Paper variant="outlined" sx={{p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography><strong>{defaultReceiptPrinter.name}</strong></Typography>
                  <Button variant="outlined" color="error" size="small" onClick={onRemoveDefaultPrinter}>{t('printingSettingsTab.common.remove')}</Button>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{mb:1}}> 
                  {t('printingSettingsTab.singleMode.selectDefaultHint')}
                </Typography>
              )}

              {/* Local Printers Section for Single Mode */} 
              <Typography variant="h6" sx={{ mt: 2.5, mb: 1, fontSize: '1rem' }}>{t('printingSettingsTab.localPrinters.title')}</Typography>
              <Button 
                size="small" 
                variant="outlined"
                onClick={onRefreshLocalPrinters}
                sx={{mb: 1.5}}
                disabled={loadingLocalPrinters}
              >
                {loadingLocalPrinters ? <CircularProgress size={16} sx={{mr:1}} /> : null}
                {t('printingSettingsTab.localPrinters.refresh')}
              </Button>

              {loadingLocalPrinters && !localPrintersError && <CircularProgress size={20} />}
              {localPrintersError && <Alert severity="error" sx={{mb:1}}>{localPrintersError}</Alert>}
              {!loadingLocalPrinters && !localPrintersError && localPrinters.length === 0 && (
                <Typography variant="caption" color="text.secondary">{t('printingSettingsTab.localPrinters.notFound')}</Typography>
              )}
              {!loadingLocalPrinters && !localPrintersError && localPrinters.length > 0 && (
                <List dense disablePadding component={Paper} variant="outlined" sx={{maxHeight: 200, overflow: 'auto'}}>
                  {localPrinters.map(printer => (
                    <ListItem 
                      key={printer.id} 
                      divider
                      secondaryAction={
                        <Box>
                          <Button 
                            size="small" 
                            variant="text"
                            color="primary"
                            onClick={() => onPrintTestPage(printer.id, printer.name)}
                            disabled={printTestLoading === printer.id}
                            sx={{fontSize: '0.7rem', p: '2px 4px', mr: 1}}
                          >
                            {printTestLoading === printer.id ? t('printingSettingsTab.localPrinters.printing') : t('printingSettingsTab.localPrinters.test')}
                          </Button>
                          <Button 
                            size="small" 
                            variant="text"
                            onClick={() => onSetDefaultPrinter(printer)}
                            disabled={defaultReceiptPrinter?.name === printer.name}
                            sx={{fontSize: '0.7rem', p: '2px 4px'}}
                          >
                            {defaultReceiptPrinter?.name === printer.name ? t('printingSettingsTab.localPrinters.default') : t('printingSettingsTab.localPrinters.set')}
                          </Button>
                        </Box>
                      }
                      sx={{ pl:1.5, pr:1, py: 0.5 }}
                    >
                      <ListItemText primary={printer.name} sx={{m:0}}/>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="body2" color="text.secondary">
                {t('printingSettingsTab.singleMode.description')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
      
      {/* Kategori Bazlı Mod İçeriği */}
      <Box sx={{ mt: 3, opacity: printingMode === 'category' ? 1 : 0.5, pointerEvents: printingMode === 'category' ? 'auto' : 'none' }}>
        <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', color: printingMode === 'category' ? 'text.primary' : 'text.disabled' }}>
          {t('printingSettingsTab.categoryMode.title')}
        </Typography>
        {printingMode === 'category' && (
          <Grid container spacing={3}>
            <Grid item xs={12}> 
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('printingSettingsTab.categoryMode.description')}
              </Typography>

              <Button 
                size="small" 
                variant="outlined"
                onClick={onRefreshLocalPrinters}
                sx={{mb: 1.5}}
                disabled={loadingLocalPrinters}
              >
                {loadingLocalPrinters ? <CircularProgress size={16} sx={{mr:1}} /> : null}
                {t('printingSettingsTab.localPrinters.refresh')}
              </Button>

              {loadingLocalPrinters && !localPrintersError && <CircularProgress size={20} sx={{display: 'block', margin: 'auto'}}/>}
              {localPrintersError && <Alert severity="error" sx={{mb:1.5}}>{localPrintersError}</Alert>}

              {/* ✅ YENİ: Kategori bazlı modda da yerel yazıcı listesi (test yazdırma için) */}
              {!loadingLocalPrinters && !localPrintersError && localPrinters.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 600 }}>
                    {t('printingSettingsTab.localPrinters.title')}
                  </Typography>
                  <List dense disablePadding component={Paper} variant="outlined" sx={{ maxHeight: 180, overflow: 'auto' }}>
                    {localPrinters.map(printer => (
                      <ListItem
                        key={printer.id}
                        divider
                        secondaryAction={
                          <Button
                            size="small"
                            variant="text"
                            color="primary"
                            onClick={() => onPrintTestPage(printer.id, printer.name)}
                            disabled={printTestLoading === printer.id}
                            sx={{ fontSize: '0.7rem', p: '2px 4px' }}
                          >
                            {printTestLoading === printer.id ? t('printingSettingsTab.localPrinters.printing') : t('printingSettingsTab.localPrinters.test')}
                          </Button>
                        }
                        sx={{ pl: 1.5, pr: 1, py: 0.5 }}
                      >
                        <ListItemText primary={printer.name} sx={{ m: 0 }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Kategori-Yazıcı ataması tablosu */}
              {!loadingLocalPrinters && !localPrintersError && categoriesToListForPrinting.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                        <TableCell>{t('printingSettingsTab.table.categoryName')}</TableCell>
                        <TableCell sx={{width: onCategoryPrinterChange2 ? '35%' : '55%'}}>{t('printingSettingsTab.table.assignedPrinter')}</TableCell>
                        {onCategoryPrinterChange2 && (
                          <TableCell sx={{ width: '35%' }}>{t('printingSettingsTab.table.assignedPrinter2')}</TableCell>
                        )}
                        <TableCell sx={{ width: 90, textAlign: 'center' }}>{t('printingSettingsTab.localPrinters.test')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoriesToListForPrinting.map(category => {
                        const assignedPrinterId = categoryPrinterAssignments[category.id];
                        const assignedPrinter = localPrinters.find(p => p.id === assignedPrinterId) || null;
                        const assignedPrinterId2 = categoryPrinterAssignments2?.[category.id];
                        const assignedPrinter2 = localPrinters.find(p => p.id === assignedPrinterId2) || null;
                        return (
                          <TableRow key={category.id}>
                            <TableCell component="th" scope="row">{category.name}</TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={localPrinters} 
                                getOptionLabel={(option) => option.name}
                                value={assignedPrinter}
                                onChange={(event, newValue) => {
                                  onCategoryPrinterChange(category.id, newValue ? newValue.id : null);
                                }}
                                renderInput={(params) => 
                                  <TextField 
                                    {...params} 
                                    variant="standard" 
                                    placeholder={localPrinters.length > 0 ? t('printingSettingsTab.table.selectPrinter') : t('printingSettingsTab.table.refreshPrintersFirst')} 
                                  />
                                }
                                sx={{ minWidth: 180 }}
                                ListboxProps={{ style: { maxHeight: 180, overflow: 'auto' } }}
                                disabled={localPrinters.length === 0 && !loadingLocalPrinters} 
                                noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
                              />
                            </TableCell>
                            {onCategoryPrinterChange2 && (
                              <TableCell>
                                <Autocomplete
                                  size="small"
                                  options={localPrinters.filter(p => p.id !== assignedPrinterId)}
                                  getOptionLabel={(option) => option.name}
                                  value={assignedPrinter2}
                                  onChange={(event, newValue) => {
                                    onCategoryPrinterChange2(category.id, newValue ? newValue.id : null);
                                  }}
                                  renderInput={(params) =>
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      placeholder={localPrinters.length > 0 ? t('printingSettingsTab.table.selectPrinter2') : t('printingSettingsTab.table.refreshPrintersFirst')}
                                    />
                                  }
                                  sx={{ minWidth: 180 }}
                                  ListboxProps={{ style: { maxHeight: 180, overflow: 'auto' } }}
                                  disabled={localPrinters.length === 0 && !loadingLocalPrinters}
                                  noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={() => assignedPrinter && onPrintTestPage(assignedPrinter.id, assignedPrinter.name)}
                                disabled={!assignedPrinter || printTestLoading === assignedPrinter?.id}
                                sx={{ fontSize: '0.7rem', p: '2px 4px', minWidth: 0 }}
                              >
                                {assignedPrinter && printTestLoading === assignedPrinter.id ? t('printingSettingsTab.localPrinters.printing') : t('printingSettingsTab.localPrinters.test')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* Adisyon / Ana Fiş Yazıcısı Seçimi (Kategori Bazlı Modda - Tüm Modlar) */}
              <Box sx={{ mt: 3, p: 2, bgcolor: isRestaurantMode ? 'warning.light' : 'info.light', borderRadius: 1, border: '1px solid', borderColor: isRestaurantMode ? 'warning.main' : 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ReceiptLongIcon sx={{ mr: 1, color: isRestaurantMode ? 'warning.dark' : 'info.dark' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color={isRestaurantMode ? 'warning.dark' : 'info.dark'}>
                    {isRestaurantMode ? t('printingSettingsTab.adisyonPrinterTitle') : t('printingSettingsTab.mainReceiptPrinterTitle')}
                  </Typography>
                  {isRestaurantMode && (
                    <Chip 
                      label={t('restaurantScreen.restaurant')} 
                      size="small" 
                      color="warning" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isRestaurantMode 
                    ? t('printingSettingsTab.categoryModeRestaurantDescription')
                    : t('printingSettingsTab.categoryModeQuickSaleDescription')
                  }
                </Typography>
                
                {adisyonPrinter ? (
                  <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PrintIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography fontWeight="bold">{adisyonPrinter.name}</Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      onClick={() => onAdisyonPrinterChange?.(null)}
                    >
                      {t('printingSettingsTab.common.remove')}
                    </Button>
                  </Paper>
                ) : (
                  <Autocomplete
                    size="small"
                    options={localPrinters}
                    getOptionLabel={(option) => option.name}
                    value={null}
                    onChange={(event, newValue) => {
                      onAdisyonPrinterChange?.(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={localPrinters.length > 0 ? (isRestaurantMode ? t('printingSettingsTab.selectAdisyonPrinterPlaceholder') : t('printingSettingsTab.selectMainPrinterPlaceholder')) : t('printingSettingsTab.table.refreshPrintersFirst')}
                        sx={{ bgcolor: 'background.paper' }}
                      />
                    )}
                    disabled={localPrinters.length === 0 && !loadingLocalPrinters}
                    noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
                  />
                )}
              </Box>

              {/* Kategorisiz Ürün Yazıcısı (Kategori Bazlı Mod) — kategorisi olmayan ürünler buraya gider */}
              {onUncategorizedPrinterChange && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PrintIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('printingSettingsTab.uncategorizedPrinterTitle')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('printingSettingsTab.uncategorizedPrinterDescription')}
                  </Typography>

                  {uncategorizedPrinter ? (
                    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PrintIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography fontWeight="bold">{uncategorizedPrinter.name}</Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => onUncategorizedPrinterChange?.(null)}
                      >
                        {t('printingSettingsTab.common.remove')}
                      </Button>
                    </Paper>
                  ) : (
                    <Autocomplete
                      size="small"
                      options={localPrinters}
                      getOptionLabel={(option) => option.name}
                      value={null}
                      onChange={(event, newValue) => {
                        onUncategorizedPrinterChange?.(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={localPrinters.length > 0 ? t('printingSettingsTab.selectUncategorizedPrinterPlaceholder') : t('printingSettingsTab.table.refreshPrintersFirst')}
                          sx={{ bgcolor: 'background.paper' }}
                        />
                      )}
                      disabled={localPrinters.length === 0 && !loadingLocalPrinters}
                      noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
                    />
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
      
      {/* ✅ YENİ: Yazarkasa + Fiş Yazıcısı Birlikte Kullanım Ayarı */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PointOfSaleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('printingSettingsTab.useReceiptPrinterWithFiscal')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('printingSettingsTab.useReceiptPrinterWithFiscalDescription')}
              </Typography>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={useBothFiscalAndReceiptPrinter}
                onChange={(e) => onUseBothFiscalAndReceiptPrinterChange?.(e.target.checked)}
                color="primary"
              />
            }
            label={useBothFiscalAndReceiptPrinter ? t('printingSettingsTab.active') : t('printingSettingsTab.inactive')}
            labelPlacement="start"
          />
        </Box>
        
        {useBothFiscalAndReceiptPrinter && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('printingSettingsTab.warningTitle')}</strong> {t('printingSettingsTab.warningDescription')}
              <br />• {t('printingSettingsTab.warningStep1')}
              <br />• {printingMode === 'single' ? t('printingSettingsTab.warningStep2Single') : t('printingSettingsTab.warningStep2Category')}
            </Typography>
          </Alert>
        )}
      </Box>

      {/* ✅ YENİ: Para Çekmecesi Tetik Yazıcısı (opsiyonel — sadece callback verildiğinde görünür) */}
      {onCashDrawerPrinterChange && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <PaymentsIcon sx={{ mr: 1, color: 'success.main' }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('printingSettingsTab.cashDrawerTriggerPrinter')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('printingSettingsTab.cashDrawerTriggerDescription')}
                </Typography>
              </Box>
            </Box>

            {cashDrawerPrinter ? (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PrintIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography fontWeight="bold">{cashDrawerPrinter.name}</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => onCashDrawerPrinterChange(null)}
                  >
                    {t('printingSettingsTab.common.remove')}
                  </Button>
                </Box>

                {/* Pin secimi: çekmece hangi pin'e bagliysa o tetiklenir.
                    'Otomatik' her ikisini sirayla dener (test icin pratik). */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Çekmece Pin Seçimi
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Çekmecen hangi pin'e bağlıysa onu seç. Bilmiyorsan <b>Otomatik</b>'te bırak; sistem her iki pin'i sırayla dener. Test butonuyla deneyip çalışan pin'i seçip kaydet.
                  </Typography>
                  <FormControl size="small">
                    <RadioGroup
                      row
                      value={cashDrawerPrinter.pin || 'auto'}
                      onChange={(e) => {
                        const newPin = e.target.value as CashDrawerPin;
                        onCashDrawerPrinterChange({ ...cashDrawerPrinter, pin: newPin });
                      }}
                    >
                      <FormControlLabel
                        value="auto"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">Otomatik</Typography>
                            <Typography variant="caption" color="text.secondary">Her iki pin'i dener</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="pin2"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">Pin 2 (m=0)</Typography>
                            <Typography variant="caption" color="text.secondary">XPrinter, Epson</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="pin5"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">Pin 5 (m=1)</Typography>
                            <Typography variant="caption" color="text.secondary">Afanda, Aafnet, bazı Hisense</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Test butonlari: hem secili pini hem de digerini ayri ayri
                    deneyebilmek icin 3 buton; calisan ile ayni hizada secimi
                    yapip kaydetmen yeterli. */}
                {onTestCashDrawer && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => onTestCashDrawer(cashDrawerPrinter.name, cashDrawerPrinter.pin || 'auto')}
                      disabled={cashDrawerTestLoading}
                      startIcon={cashDrawerTestLoading ? <CircularProgress size={14} /> : undefined}
                    >
                      {cashDrawerTestLoading ? t('printingSettingsTab.sending') : `${t('printingSettingsTab.testDrawer')} (Seçili)`}
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => onTestCashDrawer(cashDrawerPrinter.name, 'pin2')}
                      disabled={cashDrawerTestLoading}
                    >
                      Sadece Pin 2 Dene
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => onTestCashDrawer(cashDrawerPrinter.name, 'pin5')}
                      disabled={cashDrawerTestLoading}
                    >
                      Sadece Pin 5 Dene
                    </Button>
                  </Box>
                )}
              </Paper>
            ) : (
              <Autocomplete
                size="small"
                options={localPrinters}
                getOptionLabel={(option) => option.name}
                value={null}
                onChange={(event, newValue) => {
                  // Yeni secimde varsayilan pin = 'auto'.
                  onCashDrawerPrinterChange(newValue ? { ...newValue, pin: 'auto' } : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={localPrinters.length > 0 ? t('printingSettingsTab.selectCashDrawerPrinter') : t('printingSettingsTab.table.refreshPrintersFirst')}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                )}
                disabled={localPrinters.length === 0 && !loadingLocalPrinters}
                noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
              />
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PrintingSettingsTab; 